
// src/pages/CriticalOperation/TimeHistory.jsx

import React, { useEffect, useMemo, useState } from "react";

import {
  DatePicker,
  Select,
  Button,
  Row,
  Col,
  Typography,
  Spin,
  Table,
  Divider,
  Card,
  Empty,
  message,
  Tooltip,
} from "antd";

import {
  SendOutlined,
  ReloadOutlined,
  CalendarOutlined,
  ThunderboltOutlined,
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
} from "@ant-design/icons";

import dayjs from "dayjs";
import "antd/dist/reset.css";
import "../../css/sm-art-common.css";

import CriticalOperationService from "../../services/CriticalOperationService";

const { Title, Text } = Typography;

const TABLE_PAGE_SIZE = 50;

const TimeHistory = () => {
  const [fromDate, setFromDate] = useState(dayjs());
  const [toDate, setToDate] = useState(dayjs());

  const [deviceList, setDeviceList] = useState([]);
  const [selectedDevice, setSelectedDevice] = useState(undefined);

  const [groupedData, setGroupedData] = useState({});
  const [deviceCounts, setDeviceCounts] = useState({});

  const [loading, setLoading] = useState(false);
  const [deviceLoading, setDeviceLoading] = useState(false);

  const formatDate = (datetime) =>
    datetime
      ? dayjs(datetime).format("DD-MM-YYYY")
      : "-";

  const formatTime = (datetime) =>
    datetime
      ? dayjs(datetime).format("hh:mm:ss A")
      : "-";

  const normalizeGapStatus = (value) =>
    String(value ?? "")
      .trim()
      .toLowerCase();

  const getPcsValue = (row) =>
    Number(
      row?.pcs ??
        row?.piece ??
        row?.pieces ??
        row?.qty ??
        row?.quantity ??
        row?.total_pcs ??
        0
    );

  const dropdownSpinner = (
    <div className="dark-dropdown-spinner">
      <Spin size="small" />
    </div>
  );

  useEffect(() => {
    const loadDevices = async () => {
      setDeviceLoading(true);

      try {
        const response =
          await CriticalOperationService.getDevices();

        if (!response?.ok) {
          setDeviceList([]);
          message.error("Failed to load devices");
          return;
        }

        const devices =
          response?.data?.data?.devices || [];

        setDeviceList(
          Array.isArray(devices) ? devices : []
        );
      } catch (error) {
        console.error("Device API error:", error);
        setDeviceList([]);
        message.error("Device API error");
      } finally {
        setDeviceLoading(false);
      }
    };

    loadDevices();
  }, []);

  const columns = useMemo(
    () => [
      {
        title: "DO ID",
        dataIndex: "do_id",
        key: "do_id",
        align: "center",
        width: 120,
        render: (value) => value || "-",
      },
      {
        title: "Operation Date",
        dataIndex: "op_date_time",
        key: "operation_date",
        align: "center",
        width: 160,
        render: (value) => formatDate(value),
      },
      {
        title: "Operation Time",
        dataIndex: "op_date_time",
        key: "operation_time",
        align: "center",
        width: 170,
        render: (value) => formatTime(value),
      },
      {
        title: "PCS",
        dataIndex: "pcs",
        key: "pcs",
        align: "center",
        width: 100,
        render: (_, record) =>
          getPcsValue(record),
      },
      {
        title: "Status",
        dataIndex: "gap_status",
        key: "gap_status",
        align: "center",
        width: 130,
        render: (value) => {
          const status = normalizeGapStatus(value);

          if (status === "red") {
            return (
              <span className="sm-pill sm-pill-danger">
                Delay
              </span>
            );
          }

          if (status === "green") {
            return (
              <span className="sm-pill sm-pill-success">
                Green
              </span>
            );
          }

          return (
            <span className="sm-pill sm-pill-info">
              {value || "Normal"}
            </span>
          );
        },
      },
      {
        title: "Actions",
        key: "actions",
        align: "center",
        width: 150,
        render: () => (
          <div className="sm-action-group">
            <Button
              className="sm-icon-btn sm-icon-btn-view"
              icon={<EyeOutlined />}
            />

            <Button
              className="sm-icon-btn sm-icon-btn-edit"
              icon={<EditOutlined />}
            />

            <Button
              className="sm-icon-btn sm-icon-btn-delete"
              icon={<DeleteOutlined />}
            />
          </div>
        ),
      },
    ],
    []
  );

  const loadData = async () => {
    if (!fromDate || !toDate) {
      message.warning(
        "Please select From Date and To Date"
      );
      return;
    }

    if (fromDate.isAfter(toDate, "day")) {
      message.warning(
        "From Date cannot be after To Date"
      );
      return;
    }

    setLoading(true);

    try {
      const response =
        await CriticalOperationService.getOperationsFilter(
          fromDate.format("YYYY-MM-DD"),
          toDate.format("YYYY-MM-DD"),
          selectedDevice || ""
        );

      if (!response?.ok) {
        setGroupedData({});
        setDeviceCounts({});

        message.info(
          response?.data?.message ||
            "No data found for selected filters"
        );

        return;
      }

      const operations =
        response?.data?.data?.operations || [];

      const counts =
        response?.data?.data?.device_statistics
          ?.device_counts || {};

      const grouped = (
        Array.isArray(operations) ? operations : []
      ).reduce((accumulator, operation) => {
        const deviceId =
          operation?.device_id ?? "UNKNOWN";

        if (!accumulator[deviceId]) {
          accumulator[deviceId] = [];
        }

        accumulator[deviceId].push(operation);

        return accumulator;
      }, {});

      setGroupedData(grouped);
      setDeviceCounts(counts);
    } catch (error) {
      console.error(
        "Operations API error:",
        error
      );

      setGroupedData({});
      setDeviceCounts({});

      message.error("Operations API error");
    } finally {
      setLoading(false);
    }
  };

  const setPresetToday = () => {
    const today = dayjs();

    setFromDate(today);
    setToDate(today);
  };

  const setPreset7 = () => {
    setFromDate(
      dayjs().subtract(6, "day")
    );

    setToDate(dayjs());
  };

  const setPresetMonth = () => {
    setFromDate(
      dayjs().startOf("month")
    );

    setToDate(dayjs());
  };

  const resetData = () => {
    setFromDate(dayjs());
    setToDate(dayjs());
    setSelectedDevice(undefined);
    setGroupedData({});
    setDeviceCounts({});
  };

  const renderDeviceOptions = () => {
    if (!Array.isArray(deviceList)) {
      return null;
    }

    return deviceList.map((device, index) => {
      if (typeof device === "string") {
        return (
          <Select.Option
            key={`${device}-${index}`}
            value={device}
          >
            {device}
          </Select.Option>
        );
      }

      const value =
        device?.device_id ??
        device?.id ??
        device?.device ??
        device?.name ??
        `device_${index}`;

      const label =
        device?.device_name ??
        device?.name ??
        device?.device ??
        String(value);

      return (
        <Select.Option
          key={`${value}-${index}`}
          value={String(value)}
        >
          {label}
        </Select.Option>
      );
    });
  };

  const hasData =
    Object.keys(groupedData).length > 0;

  return (
    <div className="sm-page">
      <style>{`
        .dark-dropdown-spinner {
          text-align: center;
          padding: 8px;
        }

        .dark-dropdown-spinner .ant-spin,
        .dark-dropdown-spinner .ant-spin-dot {
          color: #000 !important;
        }

        .dark-dropdown-spinner .ant-spin-dot-item {
          background-color: #000 !important;
        }

        .sm-select.ant-select .ant-select-arrow,
        .sm-select.ant-select .ant-select-suffix {
          color: #000 !important;
        }

        .sm-select.ant-select
          .ant-select-arrow
          .anticon-loading,
        .sm-select.ant-select
          .ant-select-arrow
          .anticon-spin,
        .sm-select.ant-select
          .ant-select-suffix
          .anticon-loading,
        .sm-select.ant-select
          .ant-select-suffix
          .anticon-spin,
        .sm-select.ant-select
          .ant-select-arrow
          svg,
        .sm-select.ant-select
          .ant-select-suffix
          svg {
          color: #000 !important;
          fill: #000 !important;
        }

        .sm-page
          .sm-select.ant-select
          .ant-select-arrow
          *,
        .sm-page
          .sm-select.ant-select
          .ant-select-suffix
          * {
          color: #000 !important;
          fill: #000 !important;
        }
      `}</style>

      <div className="sm-page-inner">
        <div className="sm-header">
          <div className="sm-title-wrap">
            <Title
              level={3}
              className="sm-page-title"
            >
              <ThunderboltOutlined
                style={{ color: "#2150FD" }}
              />

              Time History
            </Title>

            <Text className="sm-page-subtitle">
              Filter and view device-wise
              operation timings.
            </Text>
          </div>

          <div className="sm-top-actions">
            <Tooltip title="Today">
              <Button
                className="sm-btn-soft"
                icon={<CalendarOutlined />}
                onClick={setPresetToday}
              >
                Today
              </Button>
            </Tooltip>

            <Button
              className="sm-btn-soft"
              onClick={setPreset7}
            >
              Last 7 Days
            </Button>

            <Button
              className="sm-btn-soft"
              onClick={setPresetMonth}
            >
              This Month
            </Button>
          </div>
        </div>

        <Card
          className="sm-card"
          title={<span>Filters</span>}
          extra={
            <div className="sm-filter-actions">
              <Button
                className="sm-btn-soft"
                icon={<ReloadOutlined />}
                onClick={resetData}
                disabled={loading}
              >
                Reset
              </Button>

              <Button
                className="sm-btn-primary"
                icon={<SendOutlined />}
                onClick={loadData}
                loading={loading}
              >
                Load
              </Button>
            </div>
          }
        >
          <div className="sm-filter-grid">
            <Row gutter={[16, 16]}>
              <Col
                xs={24}
                sm={12}
                md={6}
              >
                <label className="sm-label">
                  From Date
                </label>

                <DatePicker
                  className="sm-date-picker"
                  value={fromDate}
                  onChange={(date) =>
                    setFromDate(date)
                  }
                  format="DD-MM-YYYY"
                  allowClear
                />
              </Col>

              <Col
                xs={24}
                sm={12}
                md={6}
              >
                <label className="sm-label">
                  To Date
                </label>

                <DatePicker
                  className="sm-date-picker"
                  value={toDate}
                  onChange={(date) =>
                    setToDate(date)
                  }
                  format="DD-MM-YYYY"
                  allowClear
                />
              </Col>

              <Col
                xs={24}
                sm={12}
                md={8}
              >
                <label className="sm-label">
                  Device Type
                </label>

                <Select
                  className="sm-select"
                  value={selectedDevice}
                  onChange={setSelectedDevice}
                  placeholder={
                    deviceLoading
                      ? "Loading devices..."
                      : "Select device / operation"
                  }
                  allowClear
                  showSearch
                  loading={deviceLoading}
                  notFoundContent={
                    deviceLoading
                      ? dropdownSpinner
                      : "No data"
                  }
                  optionFilterProp="children"
                  filterOption={(
                    input,
                    option
                  ) =>
                    String(
                      option?.children ?? ""
                    )
                      .toLowerCase()
                      .includes(
                        input.toLowerCase()
                      )
                  }
                >
                  {renderDeviceOptions()}
                </Select>
              </Col>
            </Row>
          </div>
        </Card>

        <Card
          className="sm-card"
          title={<span>Operation Lists</span>}
          style={{ marginTop: 16 }}
        >
          <Spin spinning={loading}>
            {!hasData ? (
              <div className="sm-empty-wrap">
                <Empty
                  description={
                    <span className="sm-empty-text">
                      No operations found. Try
                      changing filters and click{" "}
                      <b>Load</b>.
                    </span>
                  }
                />
              </div>
            ) : (
              Object.keys(groupedData).map(
                (deviceId) => {
                  const rows =
                    groupedData[deviceId] || [];

                  const count =
                    deviceCounts?.[deviceId] ??
                    rows.length;

                  const totalPcs = rows.reduce(
                    (sum, row) =>
                      sum + getPcsValue(row),
                    0
                  );

                  return (
                    <div
                      key={deviceId}
                      style={{
                        marginBottom: 24,
                      }}
                    >
                      <div className="sm-section-header">
                        <Title
                          level={5}
                          className="sm-section-title"
                        >
                          {deviceId}
                        </Title>

                        <div className="sm-chip-row">
                          <span className="sm-chip sm-chip-green">
                            Api Calls: {count}
                          </span>

                          <span className="sm-chip sm-chip-blue">
                            Count: {totalPcs}
                          </span>
                        </div>
                      </div>

                      <div className="sm-table-wrap">
                     <Table
  className="sm-table"
  dataSource={rows}
  columns={columns}
  bordered
  size="middle"
  sticky
  scroll={{ x: 900 }}
  rowKey={(record, index) =>
    record?.do_id
      ? `${record.do_id}-${record?.op_date_time ?? index}`
      : `${deviceId}-${record?.op_date_time ?? index}-${index}`
  }
  rowClassName={(record) =>
    normalizeGapStatus(record?.gap_status) === "red"
      ? "sm-row-red"
      : ""
  }
  pagination={{
    pageSize: 50,
    showSizeChanger: false,
    position: ["bottomCenter"],
    className: "sm-pagination",
    showTotal: (total, range) =>
      `${range[0]}-${range[1]} of ${total} records`,
  }}
  summary={() => (
    <Table.Summary.Row>
      <Table.Summary.Cell
        index={0}
        colSpan={3}
        align="right"
      >
        <b>Total PCS</b>
      </Table.Summary.Cell>

      <Table.Summary.Cell
        index={3}
        align="center"
      >
        <b>{totalPcs}</b>
      </Table.Summary.Cell>

      <Table.Summary.Cell index={4} />
      <Table.Summary.Cell index={5} />
    </Table.Summary.Row>
  )}
/>
                      </div>

                      <Divider
                        style={{
                          margin: "18px 0",
                        }}
                      />
                    </div>
                  );
                }
              )
            )}
          </Spin>
        </Card>
      </div>
    </div>
  );
};

export default TimeHistory;

